import './ExpandedRowAnimation.css';
import { useEffect, useRef, useState } from 'react';
import { resolveAnimationHeight } from './utils/resolveAnimationHeight';
import { SETTLE_TOLERANCE } from '../../../../../constants';

interface ExpandedRowAnimationProps {
  isClosing: boolean;
  onCloseEnd: () => void;
  skipOpenAnimation?: boolean;
  onOpenAnimationStart?: () => void;
  onHeightChange?: (height: number) => void;
  initialHeight?: number;
  children: React.ReactNode;
}

const ExpandedRowAnimation = ({
  isClosing,
  onCloseEnd,
  skipOpenAnimation,
  onOpenAnimationStart,
  onHeightChange,
  initialHeight,
  children,
}: ExpandedRowAnimationProps) => {
  const innerRef = useRef<HTMLDivElement>(null);
  // Seed with the held height on a remount so the row renders at full height
  // immediately (matches the virtualScroller's expandedSize → no scroll jump
  // during the brief loading flash).
  const [contentHeight, setContentHeight] = useState<number | null>(
    initialHeight && initialHeight > 0 ? initialHeight : null
  );
  const [isOpen, setIsOpen] = useState<boolean>(skipOpenAnimation ?? false);
  // A remount starts unsettled: ignore the transient short loading height until
  // the content paints back to (≈) the held height, then track live so tab
  // switches can both grow and shrink the row.
  const settledRef = useRef<boolean>(!(initialHeight && initialHeight > 0));

  useEffect(() => {
    // Tracks inner content height so the wrapper can transition between 0px and an explicit px value — height: auto is not animatable
    let observer: ResizeObserver | null = null;
    if (innerRef.current) {
      observer = new ResizeObserver((entries) => {
        const h = entries[0]?.contentRect.height;
        if (typeof h !== 'number') return;
        const next = Math.ceil(h);
        // While holding a remount, swallow the transient short loading height;
        // release once the content is back at (≈) the held height.
        if (
          !settledRef.current &&
          next < (initialHeight ?? 0) - SETTLE_TOLERANCE
        ) {
          return;
        }
        settledRef.current = true;
        setContentHeight(next);
        onHeightChange?.(next);
      });
      observer.observe(innerRef.current);
    }

    let frame: number | undefined;
    if (!skipOpenAnimation) {
      frame = requestAnimationFrame(() => {
        setIsOpen(true);
        onOpenAnimationStart?.();
      });
    }

    return () => {
      observer?.disconnect();
      if (frame !== undefined) cancelAnimationFrame(frame);
    };
  }, []);

  const handleTransitionEnd = (e: React.TransitionEvent<HTMLDivElement>) => {
    if (e.propertyName === 'height' && isClosing) {
      onCloseEnd();
    }
  };

  const height = resolveAnimationHeight(contentHeight, isClosing, isOpen);

  return (
    <div
      className="expanded-row-animation"
      style={{ height }}
      onTransitionEnd={handleTransitionEnd}
    >
      <div ref={innerRef} className="expanded-row-animation__inner">
        {children}
      </div>
    </div>
  );
};

export default ExpandedRowAnimation;
